import React, { useState } from 'react';
import RejoinModal from './RejoinModal';
import './ArchivedUserDetailModal.scss';

const ArchivedUserDetailModal = ({ user, onClose, onRejoinSuccess }) => {
  const [showRejoinModal, setShowRejoinModal] = useState(false);
  
  if (!user) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Not specified';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Not specified';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="archived-user-detail-modal-overlay">
      <div className="archived-user-detail-modal">
        <div className="modal-header">
          <h2>Archived User Details</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="user-header">
            <div className="user-avatar">
              {user.img ? (
                <img src={user.img} alt={user.name} />
              ) : (
                <div className="avatar-placeholder">
                  {user.name?.charAt(0) || 'U'}
                </div>
              )}
            </div>
            <div className="user-title">
              <h3>{user.name || 'Unknown'}</h3>
              <p className="designation">{user.designation || 'Not specified'}</p>
              <p className="employee-id">Employee ID: {user.empID || 'N/A'}</p>
              <p className="archived-badge">Archived User</p>
            </div>
          </div>
          
          <div className="detail-sections">
            <div className="detail-section">
              <h4>Basic Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Department</label>
                  <p>{user.organogram || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p>{user.email || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>Personal Email</label>
                  <p>{user.personalEmail || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>Date of Birth</label>
                  <p>{formatDate(user.dob)}</p>
                </div>
                <div className="detail-item">
                  <label>Marital Status</label>
                  <p>{user.maritalStatus || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>Type</label>
                  <p>{user.type || 'Not specified'}</p>
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Contact Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Phone</label>
                  <p>{user.phone || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>WhatsApp</label>
                  <p>{user.whatsApp || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>Address</label>
                  <p>{user.address || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>Country</label>
                  <p>{user.country || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>Location</label>
                  <p>{user.location || 'Not specified'}</p>
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Emergency Contact</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Name</label>
                  <p>{user.emergencyContact || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>Relationship</label>
                  <p>{user.emergencyRelation || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>Phone</label>
                  <p>{user.emergencyNumber || 'Not specified'}</p>
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Leave Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Leave Entitlement</label>
                  <p>{user.leaveEntitlement || 0} days</p>
                </div>
                <div className="detail-item">
                  <label>Leave Balance</label>
                  <p>{user.leaveBalance || 0} days</p>
                </div>
                <div className="detail-item">
                  <label>Leave Taken</label>
                  <p>{user.leaveTaken || 0} days</p>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Language Preferences</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Preferred Language</label>
                  <p>{user.preferredLang || 'Not specified'}</p>
                </div>
                <div className="detail-item">
                  <label>All Languages</label>
                  <p>{user.allLang || 'Not specified'}</p>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Social Links</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>LinkedIn</label>
                  <p>
                    {user.socialLinkedIn ? (
                      <a href={user.socialLinkedIn} target="_blank" rel="noopener noreferrer">
                        {user.socialLinkedIn}
                      </a>
                    ) : 'Not specified'}
                  </p>
                </div>
                <div className="detail-item">
                  <label>Instagram</label>
                  <p>
                    {user.socialInstagram ? (
                      <a href={user.socialInstagram} target="_blank" rel="noopener noreferrer">
                        {user.socialInstagram}
                      </a>
                    ) : 'Not specified'}
                  </p>
                </div>
                <div className="detail-item">
                  <label>Twitter</label>
                  <p>
                    {user.socialTwitter ? (
                      <a href={user.socialTwitter} target="_blank" rel="noopener noreferrer">
                        {user.socialTwitter}
                      </a>
                    ) : 'Not specified'}
                  </p>
                </div>
                <div className="detail-item">
                  <label>Website</label>
                  <p>
                    {user.socialWeb ? (
                      <a href={user.socialWeb} target="_blank" rel="noopener noreferrer">
                        {user.socialWeb}
                      </a>
                    ) : 'Not specified'}
                  </p>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Profile Descriptions</h4>
              <div className="detail-grid">
                <div className="detail-item full-width">
                  <label>Short Description</label>
                  <div 
                    className="description-content"
                    dangerouslySetInnerHTML={{ __html: user.shortDescription || 'Not specified' }}
                  />
                </div>
                <div className="detail-item full-width">
                  <label>Public Description</label>
                  <div 
                    className="description-content"
                    dangerouslySetInnerHTML={{ __html: user.publicDescription || 'Not specified' }}
                  />
                </div>
                <div className="detail-item full-width">
                  <label>Short Profile Brief</label>
                  <p>{user.shortProfileBrief || 'Not specified'}</p>
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Archive Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Archived Date</label>
                  <p>{formatTimestamp(user.archived)}</p>
                </div>
                <div className="detail-item">
                  <label>Created At</label>
                  <p>{formatTimestamp(user.createdAt)}</p>
                </div>
                <div className="detail-item">
                  <label>Last Updated</label>
                  <p>{formatTimestamp(user.lastUpdated)}</p>
                </div>
                <div className="detail-item">
                  <label>Myelin Live</label>
                  <p>{user.myelinLive ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button 
            className="rejoin-button" 
            onClick={() => setShowRejoinModal(true)}
          >
            Rejoin User
          </button>
          <button className="close-button" onClick={onClose}>Close</button>
        </div>
      </div>
      
      {showRejoinModal && (
        <RejoinModal
          user={user}
          onClose={() => setShowRejoinModal(false)}
          onSuccess={(newEmail) => {
            setShowRejoinModal(false);
            if (onRejoinSuccess) {
              onRejoinSuccess(newEmail);
            }
            onClose();
          }}
        />
      )}
    </div>
  );
};

export default ArchivedUserDetailModal;
